import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { BRANCHES } from "@/lib/data";
import { CLASS_COORDINATORS, ALT_CLASS_COORDINATORS, BRANCH_EMAILS, buildMailto } from "@/lib/whatsapp";

const DEFAULT_WHATSAPP = "918248214953"; // Fallback to Phoenix India coordinator

const STYLES = ["Hip-Hop", "Contemporary", "Bollywood", "Salsa", "Zumba", "Afro", "Gymnastics", "Dance Fitness", "Wedding", "Not sure yet"];
const LEVELS = ["Beginner", "Some experience", "Intermediate", "Advanced"];

export default function BookTrial() {
  const [form, setForm] = useState({
    name: "", age: "", phone: "", email: "",
    country: "", branch: "", dance_interest: "", experience_level: "", mode: "In-Person Studio", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitToWhatsApp = (e, useAlt = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);

    const lines = [
      "*Free Trial Booking — Phoenix Dance Academy*",
      "",
      `*Name:* ${form.name}`,
      `*Age:* ${form.age}`,
      `*Phone:* ${form.phone}`,
      `*Email:* ${form.email}`,
      `*Country:* ${form.country}`,
      `*Preferred Branch:* ${form.branch}`,
      `*Dance Interest:* ${form.dance_interest}`,
      `*Experience Level:* ${form.experience_level}`,
      `*Preferred Mode:* ${form.mode}`,
      "",
      form.message ? "*Message:*" : null,
      form.message || null,
    ].filter((x) => x !== null);

    const text = encodeURIComponent(lines.join("\n"));
    const primaryNumber = CLASS_COORDINATORS[form.branch] || DEFAULT_WHATSAPP;
    const altNumber = ALT_CLASS_COORDINATORS[form.branch];
    const targetNumber = useAlt && altNumber ? altNumber : primaryNumber;
    const url = `https://wa.me/${targetNumber}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");

    toast.success("Opening WhatsApp...");
    setTimeout(() => {
      setDone(true);
      setSubmitting(false);
    }, 400);
  };

  const submit = (e) => submitToWhatsApp(e, false);
  const submitAlt = () => submitToWhatsApp(null, true);

  const sendViaEmail = () => {
    const requiredFields = ["name", "age", "phone", "email", "country", "branch", "dance_interest", "experience_level"];
    for (const k of requiredFields) {
      if (!form[k]) {
        toast.error("Please fill all required fields before sending via email.");
        return;
      }
    }
    const subject = `Free Trial Booking — Phoenix Dance Academy (${form.branch})`;
    const body = [
      `Name: ${form.name}`,
      `Age: ${form.age}`,
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      `Country: ${form.country}`,
      `Preferred Branch: ${form.branch}`,
      `Dance Interest: ${form.dance_interest}`,
      `Experience Level: ${form.experience_level}`,
      `Preferred Mode: ${form.mode}`,
      form.message ? "" : null,
      form.message ? "Message:" : null,
      form.message || null,
    ].filter((v) => v !== null).join("\n");
    const mailto = buildMailto(form.branch, subject, body);
    if (!mailto) {
      toast.error("Email enquiry is available for USA branch only.");
      return;
    }
    window.location.href = mailto;
    toast.success("Opening your email app...");
    setTimeout(() => setDone(true), 400);
  };

  if (done) {
    return (
      <div data-testid="trial-success" className="min-h-screen flex items-center justify-center px-6 ember-bg">
        <div className="max-w-xl text-center">
          <div className="w-16 h-16 mx-auto bg-[#25D366] flex items-center justify-center mb-6">
            <MessageCircle className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-5xl lg:text-6xl text-white">Almost there.</h1>
          <p className="mt-5 text-white/70 text-lg">
            Hi {form.name?.split(" ")[0] || "dancer"} — please send the pre-filled message in the WhatsApp tab that just opened. Our team will confirm your slot within 24 hours.
          </p>
          <div className="mt-10 flex justify-center gap-3">
            <Link to="/">
              <button className="border border-white/30 text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold hover:bg-white hover:text-black transition-colors">
                Back to Home
              </button>
            </Link>
            <Link to="/classes">
              <button className="btn-phoenix bg-[#ff4500] text-white px-6 h-12 uppercase tracking-wider text-xs font-semibold">
                Explore Classes
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-trial-page" className="min-h-screen">
      <section className="relative h-[36vh] min-h-[280px] overflow-hidden">
        <img src="https://images.pexels.com/photos/36763080/pexels-photo-36763080.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Free Trial Class</p>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            Try PDA. <span className="phoenix-text">Free.</span>
          </h1>
        </div>
      </section>

      <section className="py-16 max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ What to expect</p>
            <h2 className="font-display text-3xl lg:text-4xl text-white leading-[1]">
              30 minutes.<br />Real class.<br />Zero pressure.
            </h2>
            <ul className="mt-8 space-y-4 text-white/70">
              {[
                "Try a real class with a Phoenix instructor.",
                "Get a level recommendation tailored to you.",
                "Tour the studio and meet the community.",
                "Decide afterward — no obligation, no fees.",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="font-display text-2xl phoenix-text shrink-0 leading-none">0{i + 1}</span>
                  <span className="text-sm lg:text-base leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="lg:col-span-8 bg-[#0a0a0a] border border-white/10 p-6 lg:p-10 space-y-5" data-testid="trial-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <Input testid="trial-name" value={form.name} onChange={onChange("name")} required />
              </Field>
              <Field label="Age" required>
                <Input testid="trial-age" value={form.age} onChange={onChange("age")} required placeholder="e.g., 16" />
              </Field>
              <Field label="Phone" required>
                <Input testid="trial-phone" value={form.phone} onChange={onChange("phone")} required />
              </Field>
              <Field label="Email" required>
                <Input testid="trial-email" type="email" value={form.email} onChange={onChange("email")} required />
              </Field>
              <Field label="Country" required>
                <Select testid="trial-country" value={form.country} onChange={onChange("country")} required>
                  <option value="">Select country</option>
                  <option>India</option>
                  <option>United States</option>
                  <option>Canada</option>
                  <option>Other</option>
                </Select>
              </Field>
              <Field label="Preferred Branch" required>
                <Select testid="trial-branch" value={form.branch} onChange={onChange("branch")} required>
                  <option value="">Select branch</option>
                  {BRANCHES.map((b) => (
                    <option key={b.id} value={b.name}>{b.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Dance Interest" required>
                <Select testid="trial-style" value={form.dance_interest} onChange={onChange("dance_interest")} required>
                  <option value="">Select style</option>
                  {STYLES.map((s) => (<option key={s}>{s}</option>))}
                </Select>
              </Field>
              <Field label="Experience Level" required>
                <Select testid="trial-level" value={form.experience_level} onChange={onChange("experience_level")} required>
                  <option value="">Select level</option>
                  {LEVELS.map((s) => (<option key={s}>{s}</option>))}
                </Select>
              </Field>
            </div>

            <Field label="Preferred Mode">
              <div className="flex gap-2" data-testid="trial-mode">
                {["In-Person Studio", "Online", "Either"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`trial-mode-${m.toLowerCase().replace(/\s|-/g, "-")}`}
                    onClick={() => setForm({ ...form, mode: m })}
                    className={`flex-1 h-12 text-xs uppercase tracking-wider font-semibold border transition-colors ${
                      form.mode === m ? "bg-[#ff4500] border-[#ff4500] text-white" : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Message (optional)">
              <Textarea testid="trial-message" rows={3} value={form.message} onChange={onChange("message")} placeholder="Anything else we should know?" />
            </Field>

            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <button data-testid="trial-submit" type="submit" disabled={submitting} className="btn-phoenix bg-[#25D366] text-white flex-1 min-w-[200px] h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <MessageCircle className="w-4 h-4" />
                {submitting ? "Opening WhatsApp..." : (<>Book via WhatsApp <ChevronRight className="w-4 h-4" /></>)}
              </button>
              {ALT_CLASS_COORDINATORS[form.branch] && (
                <button
                  type="button"
                  onClick={submitAlt}
                  data-testid="trial-submit-alt"
                  className="btn-phoenix border border-[#25D366] text-white flex-1 min-w-[200px] h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#25D366]"
                >
                  <MessageCircle className="w-4 h-4" /> Alt WhatsApp
                </button>
              )}
              {BRANCH_EMAILS[form.branch] && (
                <button
                  type="button"
                  onClick={sendViaEmail}
                  data-testid="trial-submit-email"
                  className="btn-phoenix bg-[#ff4500] text-white flex-1 min-w-[200px] h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" /> Book via Email
                </button>
              )}
            </div>
            <p className="text-[11px] text-white/40 text-center">Sends your details to your selected branch's coordinator. We'll confirm your slot within 24 hours.</p>
          </form>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">
      {label} {required && <span className="text-[#ff4500]">*</span>}
    </span>
    {children}
  </label>
);

const Input = ({ testid, ...props }) => (
  <input data-testid={testid} {...props} className="w-full bg-black border border-white/15 px-4 h-12 text-white placeholder:text-white/30 outline-none focus:border-[#ff4500] transition-colors" />
);
const Textarea = ({ testid, ...props }) => (
  <textarea data-testid={testid} {...props} className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-[#ff4500] transition-colors resize-none" />
);
const Select = ({ testid, children, ...props }) => (
  <select data-testid={testid} {...props} className="w-full bg-black border border-white/15 px-4 h-12 text-white outline-none focus:border-[#ff4500] transition-colors appearance-none">
    {children}
  </select>
);
